import Head from "next/head";
import Link from "next/link";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I claim my tickets?",
    answer:
      "You will receive a text message the day before each game asking if you will be attending the next game. You will need to log in to your Kings Pass account to check in for each game. If you indicate that you’re attending that game, you will receive a text message a few hours before the game with a link to your tickets with your seat location. After checking in you will have the opportunity to purchase guest tickets & upgrades if they are available.",
  },
  {
    question: "Do I get a ticket to each game on the schedule?",
    answer:
      "Tickets are guaranteed for the games defined in the subscription. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket. Please note that your Kings Pass Subscription only guarantees you tickets to games you have checked in for. You can review your monthly schedule on your homepage.",
  },
  {
    question: "How do I access my tickets?",
    answer: (
      <>
        If you indicate that you&apos;re attending that game, you will receive a
        text message a few hours before the game with a link to your tickets
        with your seat location. You can access your tickets by going to{" "}
        <Link target="_blank" href="http://kings.com/ClickTix">
          Kings.com/ClickTix
        </Link>{" "}
        or via the{" "}
        <Link target="_blank" href="https://www.nba.com/kings/app">
          Kings app
        </Link>{" "}
        and logging into your account
      </>
    ),
  },
  {
    question: "How are tickets sent to me?",
    answer: (
      <>
        A few hours prior to the game, you will receive a text confirming that
        tickets have been delivered to your{" "}
        <Link target="_blank" href="https://am.ticketmaster.com/sackings/">
          Kings.com/ClickTix
        </Link>{" "}
        account. You can access your tickets by going to{" "}
        <Link target="_blank" href="http://kings.com/ClickTix">
          Kings.com/ClickTix
        </Link>{" "}
        or via the{" "}
        <Link target="_blank" href="https://www.nba.com/kings/app">
          Kings app
        </Link>{" "}
        and logging into your account
      </>
    ),
  },
  {
    question: "Where will my seats be located?",
    answer:
      "You are guaranteed a seat in the upper level, but your specific seat location will likely vary game to game. Please note that seats will be randomly assigned to pass buyers for each game, so specific seat locations cannot be guaranteed.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Sacramento Kings Pass tickets, as well as seat upgrades and guest passes, are not eligible for resale or transfer. Abusing these guidelines may result in your Kings Pass Subscription being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets have been sent to your account, they cannot be changed.
        Please contact{" "}
        <Link href="mailto:boxoffice@kings.com">boxoffice@kings.com</Link> for
        any assistance you may require.
      </>
    ),
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer: (
      <>
        Yes, you can create a group to link with other Kings Pass Delta Dental
        pass holders! Navigate to the{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "sacramentokings_deltadental" },
          }}
        >
          My Group
        </Link>{" "}
        tab in your menu and copy the invite. This will ensure you are seated
        together for the game. You will need to ensure your group is created and
        all members have joined at least 4 hours prior to game start in order to
        be seated with one another
      </>
    ),
  },
  {
    question: "Can I bring a friend to a game?",
    answer:
      "Guest tickets & upgrades will be available for select games. If available, guest tickets are available for purchase when you check in for games.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer:
      "No, we do not issue any refunds for games you are not able to attend. Passes are non-transferable and non-refundable.",
  },
  {
    question:
      "How can I change my payment method or update my expiration date?",
    answer: (
      <>
        You can update your payment method or expiration date by going to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "sacramentokings_deltadental" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab in your menu. You will see a section to update your payment method
        where you can update new information.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => {
  const theme = useSSTheme();
  return (
    <>
      By checking this box, you acknowledge and agree that your pass will
      automatically renew each month unless you opt out during the designated
      opt-out window. You also agree to the{" "}
      <Link
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
        target="_blank"
        href="https://www.attend.tech/terms-of-use"
      >
        Terms of Use
      </Link>{" "}
      and
      <Link
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
        target="_blank"
        href="https://www.attend.tech/privacy-policy"
      >
        Privacy Policy
      </Link>
      .
    </>
  );
};

export const TERMS_AND_CONDITION_OPT_OUT_RESTRICTED = () => {
  const theme = useSSTheme();
  return (
    <>
      By purchasing you understand that you are agreeing to a renewal plan for
      the 2025-26 season. You will be charged 6 installments of $99, one today
      and with 5 payments made during the rest of the season. Please note with
      this renewal offer, you are committing to the season-long Kings Pass for
      the 2025-26 season. You will not be able to cancel or request a refund
      once you have renewed your Kings Pass. Tickets are non-transferable and
      not eligible for resale. You also agree to receive SMS alerts regarding
      ticket delivery and agree to the{" "}
      <Link
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
        target="_blank"
        href="https://www.attend.tech/terms-of-use"
      >
        Terms of Use
      </Link>{" "}
      and{" "}
      <Link
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
        target="_blank"
        href="https://www.attend.tech/privacy-policy"
      >
        Privacy Policy
      </Link>
      .
    </>
  );
};

export const TERMS_AND_CONDITION_ADD_ON = () => {
  const theme = useSSTheme();
  return (
    <>
      Tickets are non-transferable and not eligible for resale. You also agree
      to receive SMS alerts regarding ticket delivery and agree to the{" "}
      <Link
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
        target="_blank"
        href="https://www.attend.tech/terms-of-use"
      >
        Terms of Use
      </Link>{" "}
      and{" "}
      <Link
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
        target="_blank"
        href="https://www.attend.tech/privacy-policy"
      >
        Privacy Policy
      </Link>
      .
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1846888515544840');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                const { cartRes } = e.detail;
                fbq('track', 'Purchase', {
                  value: cartRes.totalAmount,
                  currency: 'USD',
                });
                console.log('meta pixel purchase event fired.');
              });
            `,
        }}
      />
    </Head>
  );
};

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => <>You&apos;re all set to experience the action!</>,
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Invite your friends! Create groups{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                here
              </span>
            </u>
          </b>
          .
        </>
      );
    },
  };
